// Copyright (c) Microsoft. All rights reserved.

import React, { ReactNode, useState } from 'react';
import {
  ChatMessageType,
  // ChatMessageType,
  AIContent,
  ChatMessage,
  ChatRole,
  isTextContent,
  isFunctionResultContent,
} from '../../../libs/models/conversations/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
// import { RootState } from '../../../redux/app/store';

// import * as utils from '../../../components//utils/TextUtils';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';

import { Avatar } from '../../../components/base';
import { Button } from '../../../components/Button';
import { Tooltip } from '../../../components/Tooltip';
import { retryMessageToConversationFromUser } from '../../../redux/features/conversations/conversationsSlice_v2';
import { MessageNavigation } from '../unit/MessageNavigation';
// import { ModelList } from '../unit/ModelList';
import { setSelectedModel } from '../../../redux/features/app/aiModelsSlice';
import { RootState } from '../../../redux/app/store';
import { IMCPTool } from '../../../libs/models/app/mcp/Tool';
// import { Link } from '../../../components/Link';
// import { MessageNavigation } from '../MessageNavigation';
import { getFileIcon, isImageFile } from '../../../libs/common';
import Image from '../../../components/Image';
import { ChatModelSelect } from '../unit/ChatModelSelect';
import { AlertType } from '../../../libs/models/AlertType';
import { showAlertToast } from '../../../redux/features/app/appSlice';
import { useChat } from '../../../libs/hooks/useChat_v2';
import { usePlugin } from '../../../libs/hooks/usePlugin';
import { hasRequiredAgentInputs } from '../../../libs/models/app/AgentInput';
import { ChatPluginStarter } from '../unit/ChatPluginStarter';
import { ExternalSource } from '../../../libs/models/studio/PluginPromptList';
import { TypingIndicator } from '../TypingIndicator';
import { Error } from '../../../components/shared/Error';
import { isFunctionCallContent, isToolApprovalRequestContent } from '../../../libs/models/agui/FunctionCallContent';
import { FunctionCallRenderer } from '../../../components/chat/chat-history/function-call/FunctionCallRenderer';
import { FunctionResultRenderer } from '../../../components/chat/chat-history/function-call/FunctionResultRenderer';
import { ApprovalRenderer } from '../../../components/chat/chat-history/function-call/ApprovalRenderer';

interface TooltipButtonProps {
  message: ReactNode;
  icon: string;
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

const TooltipButton: React.FC<TooltipButtonProps> = ({ message, icon, disabled, children, onClick, ...props }) => {
  return (
    <Tooltip theme="inverse" message={message}>
      <Button icon={icon} onClick={onClick} control textBlind disabled={disabled} {...props}>
        {children}
      </Button>
    </Tooltip>
  );
};

interface ChatHistoryItemProps {
  item: ChatMessage;
  messageIndex: number;
  currentItemId: number;

  setCurrentItemIndex: (index: number) => void;
  isMe?: boolean;
  isBot: boolean;
  canRetry: boolean;
  isTyping: boolean;
  showProgress?: boolean;
  isTest?: boolean;
}

// interface ItemContentComponentProps {
//   isBot: boolean;
//   items: IChatMessage;
//   item: IChatMessageItem;
//   currentItemId: number;
//   isTyping: boolean;
//   canRetry: boolean;
//   isTest?: boolean;
//   setCurrentItemIndex: (index: number) => void;
// }

interface ItemTextComponentProps {
  item: ChatMessage;
  isTyping: boolean;
  canRetry: boolean;
  onSuggestedQuestionClick?: (text: string) => void;
}

/** error-message: true 로 표시한 오류 문구 */
const isErrorMessageContent = (c: AIContent) =>
  (c.additionalProperties as { 'error-message'?: boolean } | undefined)?.['error-message'] === true;

/** 연속된 text content들을 하나로 합침 (사이에 줄바꿈 생기는 현상 방지) */
function mergeConsecutiveText(contents: AIContent[] | undefined): AIContent[] {
  if (!contents) return [];
  const merged: AIContent[] = [];
  for (const c of contents) {
    const last = merged[merged.length - 1];
    if (isTextContent(c) && last && isTextContent(last)) {
      last.text += c.text;
    } else {
      merged.push(isTextContent(c) ? { ...c } : c);
    }
  }
  return merged;
}

export const ItemTextComponent = React.memo(
  ({ item, isTyping, canRetry, onSuggestedQuestionClick }: ItemTextComponentProps) => {
    const dispatch = useAppDispatch();
    const copyToClipboard = async (text: string): Promise<void> => {
      await new Promise<void>(() => {
        const tempInput: HTMLTextAreaElement = document.createElement('textarea');
        tempInput.value = text;
        tempInput.style.position = 'fixed';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(tempInput);
        if (successful) {
          dispatch(showAlertToast({ altMessage: '복사되었습니다', altType: AlertType.Info }));
        } else {
          dispatch(showAlertToast({ altMessage: '복사 실패하였습니다. 다시 시도해주세요.', altType: AlertType.Info }));
        }
      });
    };

    /** 오류 문구를 별도 content 로 내려준 응답인지 (구버전 응답 구분용) */
    const hasErrorMessageContent = (item.contents ?? []).some(isErrorMessageContent);

    return (
      <>
        {/* {item.searchResult?.results?.map((result, index) => (
          <React.Fragment key={index}>
            {result.hasError || result.sourceType &&
              <MultiSearch results={result} args={item.searchResult?.queries} isAccordionOpen={canRetry} />
            }
          </React.Fragment>
        ))} */}
        {/* {item.userId == "Agent:Hrmaster" && item.functionResults && <MemberCards functionResults={item.functionResults}></MemberCards>} */}

        {mergeConsecutiveText(item.contents).map((value, index) => {
          if (isToolApprovalRequestContent(value)) {
            return <ApprovalRenderer key={index} content={value} contents={item.contents} isLast={canRetry} />;
          }
          if (isFunctionCallContent(value)) {
            return <FunctionCallRenderer key={index} content={value} isLast={canRetry} />;
          }
          if (isFunctionResultContent(value)) {
            return (
              <FunctionResultRenderer
                key={index}
                result={value.result}
                callId={(value as { callId?: string }).callId}
                contents={item.contents}
                authorUserId={item.author?.userId}
                onSuggestedQuestionClick={onSuggestedQuestionClick}
              />
            );
          }
          if (isTextContent(value)) {
            // 오류 문구만 아래 Error 박스에서 표출하고, 일반 텍스트는 그대로 보여준다.
            // 단, error-message 표시가 없는 구버전 오류 응답은 기존처럼 전체 text 를 숨긴다.
            if (hasErrorMessageContent ? isErrorMessageContent(value) : item.resultMeta?.hasError) return '';
            return (
              <ChatHistoryTextContent
                key={index}
                copyToClipboard={copyToClipboard}
                text={value.text}
                isTyping={isTyping}
              />
            );
          } else {
            return '';
          }
        })}
        {item.resultMeta?.hasError && (
          <div className="msg-content">
            {/* 상세 오류(errorMessage) 대신 content(text) 표출 */}
            {(() => {
              const texts = (item.contents ?? []).filter(isTextContent);
              // error-message 표시가 있으면 그 문구만, 없으면(구버전 응답) 기존처럼 전체 text 를 쓴다.
              const contentText = (hasErrorMessageContent ? texts.filter(isErrorMessageContent) : texts)
                .map((c) => c.text)
                .join('')
                .trim();
              return <Error message={contentText || item.resultMeta?.errorMessage}></Error>;
            })()}
          </div>
        )}

        {/* <MultiSearch result={web} args={args}/>
        <AcademicSearch result={results}></AcademicSearch>
        <YouTubeSearch results={youtube}></YouTubeSearch> */}
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.item.contents === nextProps.item.contents &&
    prevProps.canRetry === nextProps.canRetry &&
    prevProps.isTyping === nextProps.isTyping,
);
ItemTextComponent.displayName = 'ItemTextComponent';

/** ChatModelSelect에 표시할 모델 이름 (또는 ABC Lab 에이전트명) */
function getSelectedModelName(item: ChatMessage, currentItemId: number): string {
  // ABC Lab 에이전트: 사용자에게 보일 이름은 에이전트명
  if (item.requestMeta?.externalSource === ExternalSource.Aionu) {
    return item.author?.userName ?? '';
  }
  // preMessages가 있고 이전 버전을 보고 있으면 그 버전의 모델
  const target = item.preMessages && currentItemId !== item.preMessages.length ? item.preMessages[currentItemId] : item;
  return target.requestMeta?.modelString ?? target.requestMeta?.model ?? '';
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  item,
  messageIndex,
  currentItemId,
  setCurrentItemIndex,
  // isMe,
  isBot,
  canRetry,
  isTyping,
  showProgress,
  isTest,
}) => {
  const chat = useChat();
  const agent = usePlugin();
  const dispatch = useAppDispatch();
  const [messagedCopied, setMessageCopied] = useState(false);

  const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations_v2);
  const toolsState = useAppSelector((state: RootState) => state.tools) as { localMcpTools: Record<string, IMCPTool[]> };
  const tools = toolsState.localMcpTools;
  const { selectedPlugin } = useAppSelector((state: RootState) => state.plugins);

  // 버전 선택 팝오버
  const [isOpen, setIsOpen] = useState(false);
  const { models, selectedModel } = useAppSelector((state: RootState) => state.models);

  const retryOnClick = (modelName?: string) => {
    // requestMeta.model이 enum index(숫자)로 들어오는 경우가 있어 modelString 우선 사용
    const fallbackModel = item.requestMeta?.modelString ?? item.requestMeta?.model ?? '';
    const targetModel = modelName ? modelName : fallbackModel;
    dispatch(setSelectedModel(targetModel));
    dispatch(
      retryMessageToConversationFromUser({
        message: item,
        model: targetModel,
        plugin: item.author?.userId ?? '',
        mcpTools: Object.values<IMCPTool[]>(tools)
          .flat()
          .filter((c) => c.enabled),
      }),
    );
  };
  const copyOnClick = async () => {
    const value = item.contents?.reduce((acc, c) => {
      if (isTextContent(c)) {
        acc += c.text; // 또는 acc.push(c.text) 후 나중에 join
      }
      return acc;
    }, '');

    if (value) {
      await copyToClipboard(value).then(() => {
        console.log(messagedCopied);
        setMessageCopied(true);
        setTimeout(() => {
          setMessageCopied(false);
        }, 2000);
      });
    }
  };

  const copyToClipboard = React.useCallback(
    async (text: string): Promise<void> => {
      await new Promise<void>(() => {
        const tempInput: HTMLTextAreaElement = document.createElement('textarea');
        tempInput.value = text;
        tempInput.style.position = 'fixed';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(tempInput);
        if (successful) {
          dispatch(showAlertToast({ altMessage: '복사되었습니다', altType: AlertType.Info }));
        } else {
          dispatch(showAlertToast({ altMessage: '복사 실패하였습니다. 다시 시도해주세요.', altType: AlertType.Info }));
        }
      });
    },
    [dispatch],
  );

  const starterOnClick = (text: string) => {
    if (agent.agentInputForm.length > 0 && !hasRequiredAgentInputs(agent.agentInputForm, agent.inputValues)) {
      agent.setInputFormVisible(true);
      return;
    }
    const additionalProperties: Record<string, object> = {};
    if (selectedPlugin?.externalSource === ExternalSource.Aionu && Object.keys(agent.inputValues).length > 0) {
      additionalProperties['abclab-input'] = agent.inputValues;
    }
    chat.getResponse({
      value: text,
      messageType: ChatMessageType.ChatMessage,
      chatId: selectedId,
      sessionId: selectedId,
      model: selectedModel.model,
      agentId: item.author?.userId,
      additionalProperties,
    });
  };

  // useEffect(() => {
  //   const chatMain = document.querySelector('.chat-main');
  //   if (chatMain) {
  //     const tolerance = 2; // 허용 오차 설정 (브라우저 zoom 변경 시 scroll 계산값이 정확히 0으로 떨어지지 않아서 showGoEndButton 설정 이슈 있음)
  //     const handleScroll = () => {
  //       const isAtBottom = Math.abs(chatMain.scrollTop + chatMain.clientHeight - chatMain.scrollHeight) <= tolerance;
  //       if (!isAtBottom) setIsOpen(false);
  //     };

  //     chatMain.addEventListener('scroll', handleScroll);
  //     return () => {
  //       chatMain.removeEventListener('scroll', handleScroll);
  //     };
  //   }
  //   return;
  // }, []);

  return (
    <div className="chat-message" data-testid={`chat-message-${messageIndex}`}>
      <div className="msg-profile">
        <Avatar src={item.author?.profileUrl} label="" />
        <strong className="name">{item.author?.userName}</strong>
      </div>
      {isBot && (isTyping || showProgress) && (
        <>
          {item.author?.userId !== 'Plugin:AiSearch' && item.progress ? (
            <TypingIndicator progress={item.progress} step={1} />
          ) : (
            <>
              {/* {item.searchResult?.results?.map((result, index) => (
            <SearchLoadingState key={index} search={result} queries={item.searchResult?.queries}></SearchLoadingState>
          ))} */}
            </>
          )}
        </>
      )}
      <>
        {item.attachedFiles && (
          <div className="chat-attached-list">
            <ul>
              {item.attachedFiles.map((file, index) => (
                <li key={index} className="list-item">
                  {isImageFile(file.fileName) ? (
                    <div className="file-preview">
                      <img src={file.uri} alt="" />
                    </div>
                  ) : (
                    // Fragment 제거하고 div로 변경
                    <div key={`file-content-${index}`}>
                      <div className="file-icon">
                        <Image url={getFileIcon(file.fileName)} />
                      </div>
                      <div className="file">
                        <strong className="name">{file.fileName}</strong>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <ItemTextComponent
          item={!item.preMessages || currentItemId == item.preMessages?.length ? item : item.preMessages[currentItemId]}
          isTyping={isTyping}
          canRetry={canRetry}
          onSuggestedQuestionClick={starterOnClick}
        />
        {item.role == ChatRole.System && (
          <ChatPluginStarter
            starters={(item.contents?.[0]?.additionalProperties?.conversationStarters as string[]) ?? []}
            isDisabled={conversations[selectedId]?.botResponseStatus}
            useInfo={true}
            onClick={starterOnClick}
          />
        )}

        <div className="msg-controls">
          {/* {isBot && items.length > 1 && ( */}
          {isBot && item.preMessages && item.preMessages.length > 0 && (
            <MessageNavigation
              currentPage={currentItemId + 1}
              // totalPages={items.length}
              totalPages={item.preMessages.length + 1}
              onPrevClick={() => {
                setCurrentItemIndex(currentItemId - 1);
              }}
              onNextClick={() => {
                setCurrentItemIndex(currentItemId + 1);
              }}
            />
          )}

          {/* {!isTest && !item.conversationStarters && items.role !== ChatRole.System && ( */}
          {!isTest && item.role !== ChatRole.System && (
            <TooltipButton
              icon="copy"
              message="복사"
              onClick={() => {
                void copyOnClick();
              }}
            />
          )}
          {canRetry && item.role !== ChatRole.System && !isTest && (
            <>
              <TooltipButton
                icon="replay"
                message="답변 재생성"
                onClick={() => {
                  retryOnClick();
                }}
              />
              <ChatModelSelect
                data={models}
                selectedName={getSelectedModelName(item, currentItemId)}
                // selectedName={item.requestMeta?.model ?? ''}
                onSelect={retryOnClick}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                message="검색 모델 변경"
                disabled={item.requestMeta?.externalSource === ExternalSource.Aionu}
              />
            </>
          )}
          {/*2024.06.24 답변 평가 숨기기  */}
          {/* <TooltipButton
            icon="thumbs-down"
            message="이 답변이 마음에 들지 않나요?"
            aria-pressed={dislike ? true : false}
            onClick={toggleDislike}
          >
            추천안함
          </TooltipButton> */}
        </div>
      </>
      {/* <ItemContentComponent items={items} item={item} isBot={isBot} currentItemId={currentItemId} isTyping={isTyping} canRetry={canRetry} setCurrentItemIndex={setCurrentItemIndex}/> */}
    </div>
  );
};
